import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';

/**
 * Step-level blocked prompt: one or more rows in this import already exist
 * under a different entity on the customer (cross-entity duplicates). Offers
 * migrating the whole import to the customer level — where the full portfolio
 * is visible and every row can link where it lives — or aborting. Fixing the
 * data on the rows themselves also clears the block.
 */
@Component({
  selector: 'app-bill-ai-flow-migrate-prompt',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bill-ai-flow-migrate-prompt.component.html',
  styleUrl: './bill-ai-flow-migrate-prompt.component.scss'
})
export class BillAiFlowMigratePromptComponent {
  state = inject(BillAiFlowStore);

  isMigrating = signal(false);

  /** Only shown at entry points below the customer — at customer level everything is already visible. */
  visible = computed(() => {
    const level = this.state.getContext()?.level;
    if (level !== 'LEN' && level !== 'SERVICE_ADDRESS' && level !== 'METER') return false;
    return this.state.crossEntityBlockedGroupIds().size > 0;
  });

  customerLabel = computed(() =>
    this.state.selectedCustomerLabel() || this.state.getContext()?.customerName || 'this customer');

  async migrate(): Promise<void> {
    if (this.isMigrating()) return;
    this.isMigrating.set(true);
    try {
      this.state.migrateContextToCustomerLevel();
      const customerId = this.state.selectedCustomerId();
      // reuseRequest keeps merges/deletes made before migrating; the cleared
      // selections and context ids drop the old parent scoping on their own.
      await this.state.runMatch(customerId !== null ? Number(customerId) : null, { reuseRequest: true });
    } finally {
      this.isMigrating.set(false);
    }
  }

  abort(): void {
    this.state.requestClose();
  }
}
